import type { GlyphName } from "./glyphs";

/** Colour families shared by apps and agents. Each has a gradient and a tile class. */
export type Hue =
  | "blue"
  | "cyan"
  | "indigo"
  | "green"
  | "teal"
  | "purple"
  | "pink"
  | "orange"
  | "amber"
  | "red"
  | "graphite";

export interface Identity {
  hue: Hue;
  glyph: GlyphName;
  label: string;
}

/**
 * Systems the crew connects to, plus flow building blocks. Keys match the API.
 * A system is shown by its own brand logo (BrandLogo) where an open-licensed one exists; the
 * glyph here is the fallback. Everything ZeaOps draws itself stays in neutral ink, because in
 * ZeaOps colour means state. The model keeps violet and "you" keeps amber.
 */
export const APPS: Record<string, Identity> = {
  azure_monitor: { hue: "graphite", glyph: "pulse", label: "Azure Monitor" },
  grafana: { hue: "graphite", glyph: "bars", label: "Grafana" },
  dynatrace: { hue: "graphite", glyph: "target", label: "Dynatrace" },
  splunk: { hue: "graphite", glyph: "lines", label: "Splunk" },
  foglight: { hue: "graphite", glyph: "db", label: "Foglight" },
  datadog: { hue: "graphite", glyph: "pulse", label: "Datadog" },
  prometheus: { hue: "graphite", glyph: "pulse", label: "Prometheus" },
  new_relic: { hue: "graphite", glyph: "pulse", label: "New Relic" },
  elastic: { hue: "graphite", glyph: "pulse", label: "Elasticsearch" },
  opentelemetry: { hue: "graphite", glyph: "pulse", label: "OpenTelemetry Collector" },
  posthog: { hue: "graphite", glyph: "pulse", label: "PostHog" },
  sentry: { hue: "graphite", glyph: "pulse", label: "Sentry" },
  servicenow: { hue: "graphite", glyph: "ticket", label: "ServiceNow" },
  jira: { hue: "graphite", glyph: "ticket", label: "Jira" },
  linear: { hue: "graphite", glyph: "ticket", label: "Linear" },
  statuspage: { hue: "graphite", glyph: "ticket", label: "Statuspage" },
  teams: { hue: "graphite", glyph: "chat", label: "Microsoft Teams" },
  slack: { hue: "graphite", glyph: "chat", label: "Slack" },
  pagerduty: { hue: "graphite", glyph: "bell", label: "PagerDuty" },
  opsgenie: { hue: "graphite", glyph: "bell", label: "Opsgenie" },
  incident_io: { hue: "graphite", glyph: "bell", label: "incident.io" },
  azure: { hue: "graphite", glyph: "servers", label: "Microsoft Azure" },
  aws: { hue: "graphite", glyph: "servers", label: "AWS" },
  gcp: { hue: "graphite", glyph: "servers", label: "Google Cloud" },
  kubernetes: { hue: "graphite", glyph: "servers", label: "Kubernetes" },
  vmware: { hue: "graphite", glyph: "servers", label: "VMware vSphere" },
  vercel: { hue: "graphite", glyph: "servers", label: "Vercel" },
  railway: { hue: "graphite", glyph: "servers", label: "Railway" },
  flyio: { hue: "graphite", glyph: "servers", label: "Fly.io" },
  nebius: { hue: "graphite", glyph: "bolt", label: "Nebius AI Cloud" },
  daytona: { hue: "graphite", glyph: "bolt", label: "Daytona" },
  beam: { hue: "graphite", glyph: "bolt", label: "Beam" },
  powershell: { hue: "graphite", glyph: "term", label: "PowerShell remoting" },
  ssh: { hue: "graphite", glyph: "term", label: "SSH" },
  ansible: { hue: "graphite", glyph: "term", label: "Ansible" },
  azure_automation: { hue: "graphite", glyph: "bolt", label: "Azure Automation" },
  azure_devops: { hue: "graphite", glyph: "branch", label: "Azure DevOps" },
  github: { hue: "graphite", glyph: "code", label: "GitHub" },
  gitlab: { hue: "graphite", glyph: "branch", label: "GitLab" },
  bitbucket: { hue: "graphite", glyph: "branch", label: "Bitbucket" },
  jenkins: { hue: "graphite", glyph: "branch", label: "Jenkins" },
  argocd: { hue: "graphite", glyph: "branch", label: "Argo CD" },
  argo_rollouts: { hue: "graphite", glyph: "branch", label: "Argo Rollouts" },
  flux: { hue: "graphite", glyph: "branch", label: "Flux" },
  helm: { hue: "graphite", glyph: "branch", label: "Helm" },
  terraform: { hue: "graphite", glyph: "code", label: "HCP Terraform" },
  pulumi: { hue: "graphite", glyph: "code", label: "Pulumi Cloud" },
  opentofu: { hue: "graphite", glyph: "code", label: "OpenTofu" },
  azure_key_vault: { hue: "graphite", glyph: "key", label: "Azure Key Vault" },
  vault: { hue: "graphite", glyph: "key", label: "HashiCorp Vault" },
  infisical: { hue: "graphite", glyph: "key", label: "Infisical" },
  openbao: { hue: "graphite", glyph: "key", label: "OpenBao" },
  entra_id: { hue: "graphite", glyph: "shield", label: "Microsoft Entra ID" },
  okta: { hue: "graphite", glyph: "shield", label: "Okta" },
  sonarcloud: { hue: "graphite", glyph: "shield", label: "SonarQube Cloud" },
  snyk: { hue: "graphite", glyph: "shield", label: "Snyk" },
  trivy: { hue: "graphite", glyph: "shield", label: "Trivy" },
  kyverno: { hue: "graphite", glyph: "shield", label: "Kyverno" },
  sql_server: { hue: "graphite", glyph: "db", label: "SQL Server" },
  postgresql: { hue: "graphite", glyph: "db", label: "PostgreSQL" },
  oracle: { hue: "graphite", glyph: "db", label: "Oracle Database" },
  mysql: { hue: "graphite", glyph: "db", label: "MySQL" },
  mongodb: { hue: "graphite", glyph: "db", label: "MongoDB" },
  redis: { hue: "graphite", glyph: "db", label: "Redis" },
  supabase: { hue: "graphite", glyph: "db", label: "Supabase" },
  planetscale: { hue: "graphite", glyph: "db", label: "PlanetScale" },
  neon: { hue: "graphite", glyph: "db", label: "Neon" },
  clickhouse: { hue: "graphite", glyph: "db", label: "ClickHouse" },
  databricks: { hue: "graphite", glyph: "db", label: "Databricks" },
  snowflake: { hue: "graphite", glyph: "db", label: "Snowflake" },
  cloudflare: { hue: "graphite", glyph: "hub", label: "Cloudflare" },
  f5: { hue: "graphite", glyph: "hub", label: "F5 BIG-IP" },
  cilium: { hue: "graphite", glyph: "hub", label: "Cilium" },
  envoy: { hue: "graphite", glyph: "hub", label: "Envoy" },
  istio: { hue: "graphite", glyph: "hub", label: "Istio" },
  confluence: { hue: "graphite", glyph: "book", label: "Confluence" },
  notion: { hue: "graphite", glyph: "book", label: "Notion" },
  sharepoint: { hue: "graphite", glyph: "book", label: "SharePoint" },
  azure_openai: { hue: "purple", glyph: "sparkle", label: "Azure OpenAI" },
  zeallm: { hue: "purple", glyph: "sparkle", label: "ZeaLLM" },
  openai: { hue: "purple", glyph: "sparkle", label: "OpenAI" },
  anthropic: { hue: "purple", glyph: "sparkle", label: "Anthropic" },
  webhook: { hue: "graphite", glyph: "link", label: "Webhook" },
  http: { hue: "graphite", glyph: "external", label: "HTTP request" },
  schedule: { hue: "graphite", glyph: "clock", label: "Schedule" },
  decision: { hue: "graphite", glyph: "split", label: "Decision" },
  lock: { hue: "graphite", glyph: "lock", label: "Certificate" },
  doc: { hue: "graphite", glyph: "doc", label: "Knowledge" },
  case: { hue: "graphite", glyph: "lens", label: "Case" },
  flow: { hue: "graphite", glyph: "flows", label: "Flow" },
  radar: { hue: "graphite", glyph: "radar", label: "Sentinel" },
  more: { hue: "graphite", glyph: "plus", label: "More" },
  manual: { hue: "graphite", glyph: "play", label: "Started by a person" },
  /* The crew's own steps in a flow, and a person's approval. */
  investigator: { hue: "graphite", glyph: "lens", label: "Investigator" },
  resolver: { hue: "graphite", glyph: "wrench", label: "Resolver" },
  guardian: { hue: "graphite", glyph: "shield", label: "Guardian" },
  scribe: { hue: "graphite", glyph: "doc", label: "Scribe" },
  commander: { hue: "graphite", glyph: "flag", label: "Commander" },
  sentinel: { hue: "graphite", glyph: "radar", label: "Sentinel" },
  engineer: { hue: "graphite", glyph: "pen", label: "Engineer" },
  you: { hue: "orange", glyph: "person", label: "You" },
};

export const AGENTS: Record<string, Identity> = {
  sentinel: { hue: "graphite", glyph: "radar", label: "Sentinel" },
  triage: { hue: "graphite", glyph: "sort", label: "Triage" },
  resolver: { hue: "graphite", glyph: "wrench", label: "Resolver" },
  investigator: { hue: "graphite", glyph: "lens", label: "Investigator" },
  engineer: { hue: "graphite", glyph: "pen", label: "Engineer" },
  guardian: { hue: "graphite", glyph: "shield", label: "Guardian" },
  commander: { hue: "graphite", glyph: "flag", label: "Commander" },
  scribe: { hue: "graphite", glyph: "doc", label: "Scribe" },
  planner: { hue: "graphite", glyph: "hub", label: "Planner" },
  you: { hue: "orange", glyph: "person", label: "You" },
  people: { hue: "graphite", glyph: "people", label: "People" },
};

export function app(key: string): Identity {
  return APPS[key] ?? { hue: "graphite", glyph: "servers", label: key };
}

export function agent(key: string): Identity {
  return AGENTS[key] ?? { hue: "graphite", glyph: "person", label: key };
}

/** Gradient stops for each hue, used by the shared SVG defs. */
export const HUE_STOPS: Record<Hue, [string, string]> = {
  blue: ["#7FA4FF", "#2F5BEA"],
  cyan: ["#6FDDF7", "#0A8FC2"],
  indigo: ["#9C98FF", "#4E47E5"],
  green: ["#5CFFC4", "#12B886"],
  teal: ["#5FE3CF", "#0E9888"],
  purple: ["#B79CFF", "#6B3BFF"],
  pink: ["#FF9EC3", "#E5447D"],
  orange: ["#FFCB73", "#F29A16"],
  amber: ["#FFD866", "#E59A00"],
  red: ["#FF8A8A", "#E5484D"],
  graphite: ["#A3A7C0", "#5E6178"],
};

export const FAULT_BARS: Record<string, string> = {
  availability: "#7D9BFF",
  disk: "#E2A657",
  database: "#3CC9B5",
  cpu: "#E27BCB",
  application: "#9C9CA6",
  memory: "#8FD16A",
  other: "#9C9CA6",
};

export const FAULT_LABELS: Record<string, string> = {
  availability: "Availability",
  disk: "Disk",
  database: "Database health",
  cpu: "CPU",
  application: "Application",
  memory: "Memory",
  other: "Other",
};
